import React from 'react';

interface JobNoteItemProps {
  note: {
    id: number;
    note: string;
    note_type: 'general' | 'important' | 'issue' | 'resolved';
    is_pinned: boolean;
    created_at: string;
    updated_at: string;
    username: string;
    email: string;
  };
  onEdit: (id: number, note: string, noteType: string, isPinned: boolean) => void;
  onDelete: (id: number) => void;
  onTogglePin: (id: number, isPinned: boolean) => void;
}

export const JobNoteItem: React.FC<JobNoteItemProps> = ({
  note,
  onEdit,
  onDelete,
  onTogglePin,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editText, setEditText] = React.useState(note.note);
  const [editType, setEditType] = React.useState(note.note_type);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const noteTypeConfig = {
    general: { label: 'General', color: 'bg-gray-500/10 text-gray-400 border-gray-500/20' },
    important: { label: 'Important', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
    issue: { label: 'Issue', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
    resolved: { label: 'Resolved', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
  };

  const config = noteTypeConfig[note.note_type];

  const handleSave = () => {
    if (editText.trim()) {
      onEdit(note.id, editText, editType, note.is_pinned);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditText(note.note);
    setEditType(note.note_type);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(note.id);
    setShowDeleteConfirm(false);
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`
      bg-[#1a2332] rounded-lg border p-4 transition-all
      ${note.is_pinned ? 'border-blue-500/30 shadow-lg shadow-blue-500/10' : 'border-gray-700'}
    `}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-semibold text-sm">
            {note.username.substring(0, 2).toUpperCase()}
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-medium">{note.username}</span>
              <span className={`
                px-2 py-0.5 rounded text-xs font-medium border
                ${config.color}
              `}>
                {config.label}
              </span>
              {note.is_pinned && (
                <span className="material-symbols-outlined text-blue-400 text-sm">
                  push_pin
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400">{getRelativeTime(note.created_at)}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onTogglePin(note.id, !note.is_pinned)}
            className="text-gray-400 hover:text-blue-400 transition-colors"
            title={note.is_pinned ? 'Unpin' : 'Pin'}
          >
            <span className="material-symbols-outlined text-lg">
              {note.is_pinned ? 'keep' : 'keep_off'}
            </span>
          </button>
          <button
            onClick={() => setIsEditing(true)}
            className="text-gray-400 hover:text-white transition-colors"
            title="Edit"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-gray-400 hover:text-red-400 transition-colors"
            title="Delete"
          >
            <span className="material-symbols-outlined text-lg">delete</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
            rows={3}
            autoFocus
          />
          <div className="flex items-center gap-3">
            <select
              value={editType}
              onChange={(e) => setEditType(e.target.value as any)}
              className="px-3 py-1.5 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value="general">General</option>
              <option value="important">Important</option>
              <option value="issue">Issue</option>
              <option value="resolved">Resolved</option>
            </select>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-300 whitespace-pre-wrap">{note.note}</p>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400 mb-3">
            Are you sure you want to delete this note? This action cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm"
            >
              Delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

